/**
 * Address Helpers
 *
 * Utility functions for handling address data in checkout forms
 */

import { Map } from 'immutable';

import type { AddressData } from '../types/checkout.types';

/**
 * Formik form instance with field mutation capability
 */
interface FormikFormRef {
  current?: {
    setFieldValue: (fieldName: string, value: string) => void;
  };
}

/**
 * Address data that can be either Immutable Map or plain object
 */
type AddressInput = Map<string, any> | AddressData | any;

/**
 * Select an address and populate form fields with its values.
 *
 * Handles both Immutable.Map (from Redux state) and plain objects. Populates the
 * standard address subfields, optionally namespaced by a field-name prefix so
 * one populator serves both the delivery fields (prefix '') and the billing
 * fields (prefix 'billing_'):
 * - {prefix}given_name
 * - {prefix}family_name
 * - {prefix}postal_code
 * - {prefix}locality
 * - {prefix}address_line1
 * - {prefix}address_line2
 * - {prefix}organization
 *
 * @param formRef - Formik form reference
 * @param address - Address data (Immutable Map or plain object)
 * @param prefix - Field-name prefix ('' for delivery, 'billing_' for billing)
 */
const selectAddress = (
  formRef: FormikFormRef,
  address: AddressInput,
  prefix: string = '',
): void => {
  if (!formRef.current) {
    return;
  }
  const { current } = formRef;

  const getValue = (key: string, fallback: string = ''): string => {
    // Handle Immutable.Map
    if (address && typeof address.get === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return address.get(key, fallback);
    }
    // Handle plain object
    if (address && typeof address === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return address[key] ?? fallback;
    }
    return fallback;
  };

  const setField = (key: string): void => {
    current.setFieldValue(`${prefix}${key}`, getValue(key, ''));
  };

  setField('given_name');
  setField('family_name');
  setField('postal_code');
  setField('locality');
  setField('address_line1');
  setField('address_line2');
  setField('organization');
};

export default selectAddress;
